import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { UnavailableDialogComponent } from '../unavailable.dialog/unavailable.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { Width } from 'src/app/interfaces/width';
import { getAssetURL } from 'src/app/constants/assets';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { CheckoutService } from '../../services/checkout.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';

@Component({
  selector: 'app-width',
  templateUrl: './width.component.html',
  styleUrls: ['./width.component.scss']
})
export class WidthComponent {
  private scrollService = inject(ScrollService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private widthsService = inject(WidthsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private widthCards = viewChildren('WidthCard', { read: ElementRef<HTMLDivElement> });
  private container = viewChild('Container', { read: ElementRef });
  private undoRedoChangeSubscription: Subscription | null = null;
  shouldScroll = signal(true);
  // widths = WIDTHS.filter(w => VALID_ITEMS.some(v => v.width === w));;
  widths = computed(() => this.widthsService.values().sort((a,b) => a.value - b.value));
  selectedWidth = computed(() => this.selectedAttributeService.getValue().width);
  shouldAnimateSelection = false;
  availableWidths = signal<number[]>([]);
  reachableWidths = signal<number[]>([]);
  filteredWidths = signal<Width[]>([]);
  widthsToShow = computed(() => {
    const selectedWidth = this.selectedWidth();
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableWidths = this.reachableWidths();
    const widths = this.widths()
    const filteredWidths = this.filteredWidths();
    const widthsToUse = filteredWidths.length > 0 ? filteredWidths : widths;
    if (isShowingUnavailable) {
      const sortedWidths = widthsToUse.sort((a, b) => (reachableWidths.includes(b.id) ? 1 : 0) - (reachableWidths.includes(a.id) ? 1 : 0));
      return sortedWidths;
    } else {
      return widthsToUse.filter(w => reachableWidths.includes(w.id) || w.id === selectedWidth?.id);
    }
  });

  constructor() {
    effect(async () => {
      this.loadingService.load('WidthComponent::GetReachable');
      const iframeID = untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null;
      const reachableAttributes = await this.rulesService.getReachableWidths(this.selectedAttributeService.configuredItem(), iframeID);
      this.reachableWidths.set(reachableAttributes);
      this.loadingService.done('WidthComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableWidths.set(availableAttributes.widthIDs);
    }, { allowSignalWrites: true });

    effect(() => {
      this.navigationService.subtitle.set(this.selectedWidth()?.displayName ?? null);
    }, { allowSignalWrites: true });

    effect(async () => {
      const cards = this.widthCards();
      const a = await this.reachableWidths();
      const container = this.container();
      if (untracked(() => this.shouldScroll()) && container) {
        this.scrollService.scrollToPosition('widths', container.nativeElement);
        this.shouldScroll.set(a.length !== cards.length);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'width');
    }
  }

  ngAfterViewInit() {
    this.shouldAnimateSelection = true;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.undoRedoChangeSubscription?.unsubscribe();
    const container = this.container();
    if (container) {
      this.scrollService.savePosition('widths', container.nativeElement.scrollTop);
    }
  }

  async selectWidth(width: Width) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'width', width.shortName);
    }

    if (this.selectedWidth() !== width) {
      if ((await this.availableWidths()).includes(width.id)) {
        await this.selectedAttributeService.mutateWidth(width);
      } else {
        try {
          this.loadingService.load('SetClosestByWidth');
          await this.selectedAttributeService.setClosestByWidth(width);
        } catch(e) {
          console.log(e);
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'width' });
        } finally {
          this.loadingService.done('SetClosestByWidth');
        }
      }
    }
  }

  getImageURL(width: string) {
    return getAssetURL(`${width}.png`);
  }

  widthValueExtractor(width: Width) {
    return width.displayName;
  }

  filterChange(filteredWidths: any) {
    this.filteredWidths.set(filteredWidths);
  }
}
